import { useAuth } from "../contexts/AuthContext";
import { isAuthPending, isAuthenticatedUser } from "../lib/authRouting";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { ROLE_LABEL, ROLE_DESC } from "../lib/permissions";
import { ShieldCheck, Building2, Loader2, Mail } from "lucide-react";
import { LoadingIndicator } from "../components/ShellContentFrame";
import { toast } from "sonner";
import { TwoFactorSettings } from "../components/TwoFactorSettings";
import { WebhookSettings } from "../components/WebhookSettings";
import { StripeConnectSettings } from "../components/StripeConnectSettings";
import { RecipientConnectSetup } from "../components/RecipientConnectSetup";
import { SupportContactStrip } from "../components/SupportContactStrip";
import { NOREPLY_EMAIL, PARTNERSHIPS_EMAIL } from "@/lib/contactEmails";
import { ContactMailLink } from "@/components/ContactMailLink";
import { DisplayCurrencyTile } from "../components/DisplayCurrencyTile";
import { CountryRegionTile } from "../components/CountryRegionTile";
import { AvatarUpload } from "../components/AvatarUpload";
import { WorkspaceInvitePanel } from "../components/InviteWorkspaceMemberDialog";

function Tab({ active, children, onClick, testid }) {
    return (
        <button onClick={onClick} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${active ? "bg-white/[0.07] text-white" : "text-white/55 hover:text-white hover:bg-white/[0.03]"}`} data-testid={testid}>
            {children}
        </button>
    );
}

function MembersTab() {
    const { activeOrg } = useAuth();
    const [members, setMembers] = useState([]);
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        if (!activeOrg?.id) return;
        setLoading(true);
        try {
            const [a, b] = await Promise.all([
                api.get(`/orgs/${activeOrg.id}`),
                ["owner", "admin"].includes(activeOrg.role) ? api.get("/invitations", { params: { org_id: activeOrg.id } }) : Promise.resolve({ data: { invitations: [] } }),
            ]);
            setMembers(a.data.memberships || []);
            setInvites(b.data.invitations || []);
        } catch (_e) { /* ignore */ }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeOrg?.id]);

    const revoke = async (id) => {
        try {
            await api.post(`/invitations/${id}/revoke`);
            toast.success("Invitation revoked");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    const removeMember = async (member) => {
        if (!window.confirm(`Remove ${member.user_name || member.user_email} from this workspace? They will be removed from all pool split wheels.`)) return;
        try {
            await api.delete(`/orgs/${activeOrg.id}/members/${member.user_id}`);
            toast.success("Member removed");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    return (
        <div className="relative min-h-[12rem]">
            <div className={loading ? "pointer-events-none invisible" : ""} aria-hidden={loading}>
                <MembersTabContent
                    members={members}
                    invites={invites}
                    activeOrg={activeOrg}
                    revoke={revoke}
                    onRemoveMember={["owner", "admin"].includes(activeOrg?.role) ? removeMember : null}
                    onReload={load}
                />
            </div>
            {loading && (
                <div className="absolute inset-0 grid place-items-center">
                    <LoadingIndicator label="Loading members…" testId="settings-members-loading" />
                </div>
            )}
        </div>
    );
}

function MembersTabContent({ members, invites, activeOrg, revoke, onRemoveMember, onReload }) {
    return (
        <div className="space-y-6">
            <WorkspaceInvitePanel activeOrg={activeOrg} onInvited={onReload} />
            <div className="surface p-5">
                <h3 className="font-heading text-lg">Members</h3>
                <p className="text-xs text-white/45 mt-1 mb-4">Workspace teammates and their permission level.</p>
                <ul className="divide-y divide-white/[0.04]">
                    {members.map((m) => (
                        <li key={m.id || m.user_id} className="py-3 flex items-center justify-between gap-3" data-testid={`member-row-${m.user_id}`}>
                            <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{m.user_name || m.user_email}</p>
                                <p className="text-xs text-white/45 truncate">{m.user_email}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <span className="fs-pill rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white/75">{ROLE_LABEL[m.role]}</span>
                                {onRemoveMember && m.role !== "owner" && (
                                    <button
                                        onClick={() => onRemoveMember(m)}
                                        className="text-xs text-rose-400 hover:underline"
                                        data-testid={`member-remove-${m.user_id}`}
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
            {["owner", "admin"].includes(activeOrg?.role) && (
                <div className="surface p-5">
                    <h3 className="font-heading text-lg">Invitations</h3>
                    <p className="text-xs text-white/45 mt-1 mb-4">
                        Pending workspace invites from this page. See all sent and received in{" "}
                        <Link to="/invites" className="text-[#00BF0D] hover:underline">Invite Center</Link>.
                    </p>
                    {invites.length === 0 ? (
                        <p className="text-sm text-white/45">No invitations yet.</p>
                    ) : (
                        <ul className="divide-y divide-white/[0.04]">
                            {invites.map((i) => (
                                <li key={i.id} className="py-3 flex items-center justify-between gap-3" data-testid={`invite-row-${i.id}`}>
                                    <div className="min-w-0 flex items-center gap-3">
                                        <Mail className="h-4 w-4 text-white/45" />
                                        <div className="min-w-0">
                                            <p className="text-sm truncate">{i.email}</p>
                                            <p className="text-[11px] text-white/40">Invited by {i.invited_by_email} · {ROLE_LABEL[i.role]} {i.pool_name ? `· ${i.pool_name}` : ""}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className={`fs-pill rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                                            i.status === "pending" ? "bg-amber-500/10 border border-amber-500/20 text-amber-300" :
                                            i.status === "accepted" ? "bg-[#00BF0D]/10 border border-[#00BF0D]/20 text-[#00BF0D]" :
                                            "bg-white/[0.04] border border-white/10 text-white/55"
                                        }`}>{i.status}</span>
                                        {i.status === "pending" && (
                                            <button onClick={() => revoke(i.id)} className="text-xs text-rose-400 hover:underline" data-testid={`invite-revoke-${i.id}`}>Revoke</button>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}

function WalletsTab() {
    return (
        <div className="surface p-5">
            <div className="flex items-center justify-between gap-3 mb-4 fs-pill-anchor">
                <div>
                    <h3 className="font-heading text-lg">Crypto wallets (optional)</h3>
                    <p className="text-xs text-white/45 mt-1 max-w-md">Stablecoin payouts are coming in a later phase. You may pre-link a wallet now — it has no effect on ACH disbursement.</p>
                </div>
                <span className="fs-pill rounded-full bg-white/[0.04] border border-white/10 px-2 py-0.5 text-[10px] font-semibold tracking-wider uppercase text-white/55">Beta</span>
            </div>
            <p className="text-sm text-white/40">Wallet integrations are paused until stablecoin disbursement launches.</p>
        </div>
    );
}

function SecurityTab() {
    return (
        <div className="space-y-6">
            <TwoFactorSettings />
        </div>
    );
}

function PreferencesTab() {
    return (
        <div className="space-y-6">
            <CountryRegionTile />
            <DisplayCurrencyTile />
        </div>
    );
}

function LeaveWorkspaceCard() {
    const { activeOrg, leaveWorkspace } = useAuth();
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    if (!activeOrg?.id || activeOrg.role === "owner") return null;

    const leave = async () => {
        if (!window.confirm(`Leave ${activeOrg.name}? You will be removed from all pool split wheels in this workspace.`)) return;
        try {
            setBusy(true);
            const result = await leaveWorkspace(activeOrg.id);
            if (!result.ok) {
                toast.error(result.error?.response?.data?.detail || result.error?.message || "Could not leave workspace");
                return;
            }
            toast.success("Left workspace");
            navigate("/dashboard", { replace: true, state: { leftWorkspace: true } });
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="surface p-5 flex items-center justify-between gap-4">
            <div>
                <h3 className="font-heading text-lg">Leave workspace</h3>
                <p className="text-sm text-white/45 mt-1">Remove yourself from {activeOrg.name} and all its pools.</p>
            </div>
            <button
                onClick={leave}
                disabled={busy}
                className="btn-ghost rounded-md px-4 py-2 text-sm text-rose-400 border border-rose-500/20 hover:bg-rose-500/10 shrink-0"
                data-testid="settings-leave-workspace"
            >
                {busy ? "Leaving…" : "Leave workspace"}
            </button>
        </div>
    );
}

function ProfileTab() {
    const { user, logout, setUserAvatar, refreshMe, sessionReady, authPhase } = useAuth();
    const ready = sessionReady && !isAuthPending(authPhase) && isAuthenticatedUser(user);

    return (
        <div className="relative min-h-[16rem]">
            <div className={ready ? "" : "pointer-events-none invisible"} aria-hidden={!ready}>
                {ready && (
                <div className="space-y-6">
            <div className="surface p-5">
                <h3 className="font-heading text-lg">Profile</h3>
                <div className="mt-4 mb-5 pb-5 border-b border-white/[0.06]">
                    <AvatarUpload
                        name={user?.name}
                        avatarUrl={user?.avatar_url}
                        uploadUrl="/users/avatar"
                        onUploaded={(data) => {
                            setUserAvatar(data.avatar_url, data.user);
                            refreshMe({ clearOnFailure: false });
                        }}
                    />
                </div>
                <p className="text-sm text-white/55 mt-3"><span className="text-white/45">Name:</span> {user?.name}</p>
                <p className="text-sm text-white/55 mt-1"><span className="text-white/45">Email:</span> {user?.email}</p>
                <p className="text-xs text-white/30 mt-2 font-mono">Member since {new Date(user?.created_at).toLocaleDateString()}</p>
            </div>
            <div className="surface p-5">
                <h3 className="font-heading text-lg">Payout receiving</h3>
                <p className="text-xs text-white/45 mt-1 mb-4 max-w-xl">
                    If you receive a share from pool payouts, complete this once so Stripe can send your portion.
                    This is separate from workspace Stripe Connect (client billing).
                </p>
                <RecipientConnectSetup />
            </div>
            <div className="surface p-5 flex items-center justify-between">
                <div>
                    <h3 className="font-heading text-lg">Session</h3>
                    <p className="text-sm text-white/45 mt-1">Sign out of this browser session.</p>
                </div>
                <button onClick={logout} className="btn-ghost rounded-md px-4 py-2 text-sm" data-testid="settings-logout">Sign out</button>
            </div>
            <LeaveWorkspaceCard />
                </div>
                )}
            </div>
            {!ready && (
                <div className="absolute inset-0 grid place-items-center">
                    <LoadingIndicator label="Loading profile…" testId="settings-profile-loading" />
                </div>
            )}
        </div>
    );
}

function normalizeSettingsTab(tab) {
    if (!tab) return null;
    if (tab === "integrations") return "connections";
    return tab;
}

function ConnectionsTab({ orgId, canConnectStripe }) {
    const [searchParams] = useSearchParams();
    const connectReturn = searchParams.get("connect");
    const [integrationsEnabled, setIntegrationsEnabled] = useState(null);

    useEffect(() => {
        if (!orgId) return;
        let cancelled = false;
        api.get("/billing/subscription", { params: { org_id: orgId } })
            .then(({ data }) => {
                if (!cancelled) setIntegrationsEnabled(!!data?.features?.integrations);
            })
            .catch(() => {
                if (!cancelled) setIntegrationsEnabled(false);
            });
        return () => { cancelled = true; };
    }, [orgId]);

    return (
        <div className="space-y-6">
            <div className="surface p-5">
                <h3 className="font-heading text-lg">Stripe Connect</h3>
                <p className="text-xs text-white/45 mt-1 mb-1 max-w-2xl leading-relaxed">
                    Links this workspace to Stripe so clients can pay by card and approved pool payouts can move through Stripe.
                    Bank details elsewhere in FairShare only record who gets what — they don&apos;t receive card payments.
                </p>
                <p className="text-[11px] text-white/35 mb-4 max-w-2xl">
                    Separate from your FairShare subscription (Billing). Client money goes to this workspace&apos;s Stripe account.
                </p>
                <StripeConnectSettings orgId={orgId} syncToken={connectReturn} canOnboard={canConnectStripe} />
            </div>
            <WebhookSettings orgId={orgId} integrationsEnabled={integrationsEnabled} />
            <div className="surface p-5">
                <h3 className="font-heading text-lg">Email (Resend)</h3>
                <p className="text-xs text-white/45 mt-1 max-w-xl">
                    Invitation and notification emails are sent from{" "}
                    <ContactMailLink email={NOREPLY_EMAIL} className="text-white/55 hover:text-[#00BF0D]" /> when Resend is
                    configured. Replies are routed to support — do not expect responses from the no-reply sender.
                </p>
                <p className="text-[11px] text-white/35 mt-2 max-w-xl">
                    Webhook and Slack/Discord setup: <ContactMailLink email={PARTNERSHIPS_EMAIL} className="text-white/45 hover:text-[#00BF0D]" />.
                </p>
                <SupportContactStrip variant="connections" className="mt-3 max-w-xl" />
            </div>
        </div>
    );
}

export function SettingsPage() {
    const { activeOrg } = useAuth();
    const [searchParams] = useSearchParams();
    const tabFromUrl = normalizeSettingsTab(searchParams.get("tab"));
    const [tab, setTab] = useState(tabFromUrl || "profile");

    useEffect(() => {
        const normalized = normalizeSettingsTab(searchParams.get("tab"));
        if (normalized && normalized !== tab) setTab(normalized);
    }, [searchParams, tab]);

    return (
        <div className="space-y-6">
            <div>
                <p className="label-eyebrow">Account</p>
                <h1 className="font-heading text-3xl sm:text-4xl tracking-tight mt-1">Settings</h1>
            </div>

            <div className="surface p-4 sm:p-5">
                <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-md bg-[#262626] border border-white/10 flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-[#00BF0D]" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold truncate">{activeOrg?.name}</p>
                        <p className="text-xs text-white/45">Your role: <span className="text-white capitalize">{activeOrg?.role}</span> · {ROLE_DESC[activeOrg?.role]}</p>
                    </div>
                    <span className="fs-pill gap-1 rounded-full bg-[#00BF0D]/8 border border-[#00BF0D]/20 px-2 py-0.5 text-[10px] font-semibold text-[#00BF0D]">
                        <ShieldCheck className="h-3 w-3" /> Verified
                    </span>
                </div>
            </div>

            <div className="flex flex-wrap gap-1 border-b border-white/[0.06] pb-2">
                <Tab active={tab === "profile"} onClick={() => setTab("profile")} testid="settings-tab-profile">Profile</Tab>
                <Tab active={tab === "security"} onClick={() => setTab("security")} testid="settings-tab-security">Security</Tab>
                <Tab active={tab === "preferences"} onClick={() => setTab("preferences")} testid="settings-tab-preferences">Preferences</Tab>
                {["owner", "admin"].includes(activeOrg?.role) && (
                    <>
                        <Tab active={tab === "members"} onClick={() => setTab("members")} testid="settings-tab-members">Workspace</Tab>
                        <Tab active={tab === "connections"} onClick={() => setTab("connections")} testid="settings-tab-connections">Connections</Tab>
                    </>
                )}
                <Tab active={tab === "wallets"} onClick={() => setTab("wallets")} testid="settings-tab-wallets">Crypto wallets</Tab>
            </div>

            {tab === "profile" && <ProfileTab />}
            {tab === "security" && <SecurityTab />}
            {tab === "preferences" && <PreferencesTab />}
            {tab === "members" && ["owner", "admin"].includes(activeOrg?.role) && <MembersTab />}
            {tab === "connections" && ["owner", "admin"].includes(activeOrg?.role) && (
                <ConnectionsTab orgId={activeOrg?.id} canConnectStripe={activeOrg?.role === "owner"} />
            )}
            {tab === "wallets" && <WalletsTab />}
        </div>
    );
}
